/**
 * The 6-question public DIAGNOSTIC bank — the single source of truth shared by
 * the diagnostic itself (`/try`, and `/` via Landing) and the answer-in-email
 * callback page (`/q`).
 *
 * It lives here rather than inside TryAQuestion so `/q` can teach from the SAME
 * content the diagnostic will grade, without importing the diagnostic's whole
 * render tree (motion, confetti, the capture form) into an email landing page.
 * TryAQuestion re-imports it verbatim; its behavior is unchanged by the move.
 *
 * Questions are curated from the "LCSW free quiz" set (src/utils/questions.json) —
 * already designated free content, written by Pam.
 */

export type DiagnosticQuestion = {
  id: number;
  cat: string;
  stem: string;
  options: { l: string; text: string }[];
  correct: string;
  why: string;
  pam: string;
};

// Pam's between-question voice deliberately stays vague on right/wrong (no "you
// got it" / "correct answer is X"): she reacts to the CONCEPT and teaches the
// exam's pattern, so the visitor can't tally a score in their head and the
// readiness reveal at the end still lands. See the `pam` line on each question.
// (On `/q` the reveal has already happened by definition — the click IS the
// reveal — so that page shows the verdict alongside her line.)
// The 2026 ASWB blueprint has THREE content areas (Values & Ethics / Assessment
// & Planning / Intervention & Practice) and each question's `cat` is exactly one
// of them (see AREAS below), 2 questions per area. This replaced an older set of
// six unique KSA-era labels, under which "your weakest area" was really just the
// first question you happened to miss. Order is deliberate: we OPEN on the
// colorism scenario (Diversity & Social Justice is competency IC *under* Values
// & Ethics in 2026) — the strongest applied question and an instant signal to a
// Black social worker that this was built for them.
//
// PENDING PAM SIGN-OFF: question id 2 (duty to warn) is a drafted replacement for
// the retired Freud psychosexual-stage recall item (off-spec for the 2026 applied
// format, and a poor first impression). Its stem/why/pam voice below are a draft
// for Pamela Coin, LCSW to approve or rewrite before this ships.
export const DIAGNOSTIC: DiagnosticQuestion[] = [
  { id: 7, cat: 'Values & Ethics', correct: 'B',
    stem: 'A 13-year-old Black client feels pressure to conform to beauty standards favoring lighter skin and straight hair. Which intervention is MOST appropriate for their body-image concerns?',
    options: [
      { l: 'A', text: 'Suggest avoiding situations where they feel judged.' },
      { l: 'B', text: 'Explore their feelings and help challenge internalized colorism and societal beauty standards.' },
      { l: 'C', text: 'Focus on academics to shift attention off appearance.' }],
    pam: 'This is a go-to-the-root question. The exam rewards naming what is really happening over smoothing it over or avoiding it.',
    why: 'The strongest response addresses the root (colorism and societal beauty standards) and empowers the client to challenge internalized messages, rather than accommodating or avoiding them.' },
  { id: 2, cat: 'Values & Ethics', correct: 'B',
    stem: 'During a session, an adult client describes a specific, credible plan to seriously harm a named former partner this week. What is the social worker\'s FIRST responsibility?',
    options: [
      { l: 'A', text: 'Preserve confidentiality and keep exploring the anger in session.' },
      { l: 'B', text: 'Take reasonable steps to protect the named person, which can include warning them and notifying authorities.' },
      { l: 'C', text: 'End the session and refer the client to a psychiatrist for evaluation.' }],
    pam: 'Confidentiality is one of our deepest commitments, right up until someone’s safety is on the line. When a threat is real, specific, and aimed at a named person, notice which answer protects that person.',
    why: 'When a client makes a serious, specific threat against an identifiable person, the duty to protect overrides confidentiality. The first responsibility is protecting the potential victim, which can include warning them and alerting authorities. Confidentiality is not absolute, and a referral alone does not address the immediate risk.' },
  { id: 10, cat: 'Assessment & Planning', correct: 'B',
    stem: 'Which of the following is a common symptom of a neurologic or organic disorder?',
    options: [{ l: 'A', text: 'Auditory hallucinations' }, { l: 'B', text: 'Memory loss' }, { l: 'C', text: 'Social withdrawal' }],
    pam: 'Organic versus psychiatric trips people up constantly. Memory is usually your loudest clue that it is the brain, not the mind.',
    why: 'Memory loss points toward neurologic or organic causes such as dementia or brain injury. Auditory hallucinations and grandiose delusions are more typical of psychiatric conditions.' },
  { id: 13, cat: 'Assessment & Planning', correct: 'B',
    stem: 'A client referred for substance use appears anxious and hesitant to discuss it. Which approach best gathers sensitive information while keeping their trust?',
    options: [
      { l: 'A', text: 'Directly ask the specifics in the first few minutes to get it quickly.' },
      { l: 'B', text: 'Build a safe, non-judgmental space, then gradually introduce the questions.' },
      { l: 'C', text: 'Avoid the topic this session; revisit later.' }],
    pam: 'Order of operations matters. On sensitive topics the exam almost always wants rapport before the hard questions.',
    why: 'Sensitive topics require trust first. Empathy and normalizing lower defensiveness so the questions can land. Confrontation or rushing breaks rapport, and avoiding the topic stalls the work.' },
  { id: 6, cat: 'Intervention & Practice', correct: 'A',
    stem: 'Behavioral Social Work Practice aims to increase desirable and decrease undesirable behavior to improve daily functioning. Which is NOT necessary in this model?',
    options: [{ l: 'A', text: 'Etiology of behavior' }, { l: 'B', text: 'Build on client strengths' }, { l: 'C', text: 'A strong therapeutic relationship' }],
    pam: 'Behavioral work lives in the here and now. When an option leans on "why it started," pause and ask whether the model even needs it.',
    why: 'Etiology (the origin or cause of a behavior) is not required in behavioral practice. The focus is the observable behavior itself and changing it, not uncovering its underlying cause.' },
  { id: 17, cat: 'Intervention & Practice', correct: 'B',
    stem: 'A son is acting out; the father is over-involved (makes his decisions) while the mother is disengaged. Per Structural Family Theory, the MOST appropriate intervention is to:',
    options: [
      { l: 'A', text: 'Do individual therapy with the son on his behaviors.' },
      { l: 'B', text: 'Reduce the father\'s over-involvement and bring the mother into family decisions to rebalance the structure.' },
      { l: 'C', text: 'Have the parents set stricter rules and consequences.' }],
    pam: 'Minuchin is about the family’s structure, not one person’s behavior. Find the boundary that is out of balance and work there.',
    why: 'Structural Family Theory (Minuchin) targets the family\'s organization and boundaries. The acting-out reflects an imbalanced structure, so the work is rebalancing roles and boundaries.' },
];

// The three 2026 content areas, in exam-weight order. Values & Ethics is the
// single largest area on every ASWB exam (36% on Clinical), so this order also
// serves as the tie-break when two areas are equally weak: a tie resolves to the
// area where a gap costs the most scored questions on the real exam. Every
// DIAGNOSTIC `cat` MUST be one of these (2 questions each).
export const AREAS = ['Values & Ethics', 'Assessment & Planning', 'Intervention & Practice'] as const;

/**
 * The bank question a `?qid=` refers to, or null when the id isn't one of ours.
 * A miss is never an error: `/q` falls back to its question-agnostic copy, which
 * is what every already-sent email link relies on.
 */
export const findDiagnosticQuestion = (qid: string | number | null | undefined): DiagnosticQuestion | null => {
  if (qid === null || qid === undefined || qid === '') return null;
  const id = typeof qid === 'number' ? qid : Number.parseInt(String(qid), 10);
  if (!Number.isFinite(id)) return null;
  return DIAGNOSTIC.find((q) => q.id === id) ?? null;
};

/** A diagnostic answer already given elsewhere (in the newsletter), to be credited. */
export type DiagnosticPrefill = { questionId: number; letter: string };

/**
 * Parses `?prefill=<questionId>:<LETTER>` — the credit `/q` hands to `/try` so a
 * visitor who answered one question in the email isn't asked it twice.
 *
 * Deliberately strict and SILENT: anything that isn't a real bank question with
 * one of that question's own option letters returns null, and /try then behaves
 * exactly as if no prefill were present. A hand-edited or truncated link must
 * degrade to the normal 6-question diagnostic, never to a broken one.
 */
export const parseDiagnosticPrefill = (raw: string | null | undefined): DiagnosticPrefill | null => {
  if (!raw) return null;
  const [rawId, rawLetter] = raw.split(':');
  if (!rawId || !rawLetter) return null;
  const question = findDiagnosticQuestion(rawId.trim());
  if (!question) return null;
  const letter = rawLetter.trim().toUpperCase();
  if (!question.options.some((o) => o.l === letter)) return null;
  return { questionId: question.id, letter };
};
